import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Description } from 'components/text/Description'
import { ExternalLinks } from 'constants/links'
import { LegalAgreementAppendixType, LegalAgreementDetailsType } from 'pages/LegalAgreements/types'
import { getListToSentancePrefix } from 'utils/utils'
import { EMIAgreement } from './AgreementBody/EMIAgreement'
import { ModulrAgreement } from './AgreementBody/ModulrAgreement'
import {
  AgreementLinkType,
  AgreementOptions,
  EMICountries,
  EMICountriesType,
  FundTransferAgreementCountries,
  ModulrCountries,
  ModulrCountriesType,
  SharedAgreementCountries,
} from './types'

export const isEMI = name => name?.includes(`_${AgreementOptions.EMI}`)
export const isMODULR = name => name?.includes(AgreementOptions.MODULR)
export const isUSFundTransferAgreement = name =>
  name?.includes(`${AgreementOptions.FUND_TRANSFER_AGREEMENT}_${FundTransferAgreementCountries.US}`)

export const getCountry = name => {
  if (name?.includes('UK')) return SharedAgreementCountries.UK
  if (name?.includes('MALTA')) return EMICountries.EEA
  if (name?.includes('EUROPE')) return ModulrCountries.EU
  return ''
}

export const useAgreementsModalData = () => {
  const { t } = useTranslation()

  const getBodyContent = (name: string, displayName: string, description: string): ReactNode => {
    const country = getCountry(name)
    if (isEMI(name)) return <EMIAgreement country={country as EMICountriesType} />
    if (isMODULR(name))
      return <ModulrAgreement displayName={displayName} country={country as ModulrCountriesType} />
    return <Description>{description}</Description>
  }

  const getLinks = (
    agreement: LegalAgreementDetailsType,
    appendixes?: LegalAgreementAppendixType[]
  ): AgreementLinkType[] => {
    const links: AgreementLinkType[] = [
      {
        label: agreement?.displayName,
        url: agreement?.url,
      },
    ]
    if (agreement?.privacyPolicyName && agreement?.privacyPolicyUrl) {
      links?.push({
        label: agreement?.privacyPolicyName,
        url: agreement?.privacyPolicyUrl,
      })
    }
    if (isEMI(agreement?.name)) {
      links?.push({
        label: t('privacyPolicy'),
        url: ExternalLinks.PRIVACY_POLICY_URL,
      })
    }
    if (appendixes?.length) {
      links?.push({
        label: appendixes?.[0]?.name ?? '',
        url: appendixes?.[0]?.url ?? '',
      })
    }
    return links
  }

  const getCheckboxLabel = (name: string, links: AgreementLinkType[]): string => {
    if (isEMI(name)) return t('emiAgreement.confirmAndAgree')
    if (isMODULR(name)) return t('modulrAgreement.confirmAndAgree')
    const agreementNames = links
      ?.map((link, linkIndex) => {
        return `${getListToSentancePrefix(links?.length, linkIndex)}${link?.label}`
      })
      ?.join('')
    return `${t('pendingAgreements.confirmAndAgree')}${agreementNames}`
  }

  return {
    getBodyContent,
    getLinks,
    getCheckboxLabel,
  }
}
